import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Markdown from "react-markdown";
import { 
  Info, 
  Sparkles, 
  Calculator, 
  TrendingUp, 
  ChevronRight, 
  BookOpen, 
  Clock, 
  AlertCircle, 
  HelpCircle, 
  ChevronDown,
  Landmark,
  Coins,
  HeartHandshake,
  Wallet,
  Receipt,
  Briefcase,
  Building,
  ShieldAlert,
  Shield,
  ArrowLeft
} from "lucide-react";
import { 
  ResponsiveContainer, 
  XAxis, 
  YAxis, 
  Tooltip as ChartTooltip, 
  BarChart, 
  Bar 
} from "recharts";

import SEO from "../seo/SEO";
import { toolsBySlug, allTools, getSlug } from "../data/allTools";
import { categories } from "../data/categories";
import { fireAndRetirementArticles } from "../data/seoArticles";
import { mortgageArticles } from "../data/mortgageArticles";
import { creditInvestmentArticles } from "../data/creditInvestmentArticles";

export default function CalculatorPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  // Find the exact calculator matching the slug path
  const tool = useMemo(() => {
    if (!slug) return null;
    return toolsBySlug[slug] || null;
  }, [slug]);

  // Find the corresponding category metadata
  const categoryMeta = useMemo(() => {
    if (!tool) return null;
    return categories.find(c => c.id === tool.category) || null;
  }, [tool]);

  const [inputVals, setInputVals] = useState<Record<string, number>>({});
  const [calculationResult, setCalculationResult] = useState<any>(null);
  const [hasCalculated, setHasCalculated] = useState<boolean>(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const currencies = useMemo(() => [
    { code: "USD", symbol: "$", rate: 1.0, label: "USD ($)" },
    { code: "EUR", symbol: "€", rate: 0.92, label: "EUR (€)" },
    { code: "GBP", symbol: "£", rate: 0.79, label: "GBP (£)" },
    { code: "INR", symbol: "₹", rate: 83.5, label: "INR (₹)" },
    { code: "CAD", symbol: "CA$", rate: 1.37, label: "CAD (CA$)" },
    { code: "AUD", symbol: "A$", rate: 1.51, label: "AUD (A$)" },
    { code: "JPY", symbol: "¥", rate: 156.5, label: "JPY (¥)" },
    { code: "MXN", symbol: "MX$", rate: 16.7, label: "MXN (MX$)" },
    { code: "CHF", symbol: "CHF", rate: 0.91, label: "CHF" },
  ], []);

  const [selectedCurrency, setSelectedCurrency] = useState(currencies[0]);

  // Initialize tool default values on page change
  useEffect(() => {
    if (tool) {
      const defaults: Record<string, number> = {};
      tool.inputs.forEach(input => {
        defaults[input.id] = input.defaultValue;
      });
      setInputVals(defaults);
      setSelectedCurrency(currencies[0]);
      
      // Auto precompute initial outputs, but don't show results until calculate button is clicked
      try {
        const initialRes = tool.calculate(defaults);
        setCalculationResult(initialRes);
        setHasCalculated(false);
      } catch (err) {
        console.error("Initial calculations error", err);
        setCalculationResult(null);
        setHasCalculated(false);
      }
      setOpenFaqIndex(null);
      window.scrollTo(0, 0);
    }
  }, [tool]);

  // Handler for custom inputs
  const handleInputChange = (fieldId: string, val: number) => {
    setInputVals(prev => {
      const updated = { ...prev, [fieldId]: val };
      return updated;
    });
    setHasCalculated(false); // Hide results when parameters are altered, requiring recalculation click
  };

  // Dedicated interactive action trigger
  const handleCalculate = () => {
    if (!tool) return;
    try {
      const finalRes = tool.calculate(inputVals);
      setCalculationResult(finalRes);
      setHasCalculated(true);
    } catch (e) {
      console.error("Manual triggered operation anomaly", e);
    }
  };

  if (!tool) {
    return (
      <div className="max-w-md mx-auto text-center py-24 px-6 space-y-6">
        <SEO 
          title="Calculator Not Found - USMoneyAI" 
          description="The requested calculator could not be found."
        />
        <div className="w-16 h-16 rounded-full bg-slate-900 border border-slate-800 flex items-center justify-center mx-auto text-cyan-400">
          <AlertCircle className="w-8 h-8" />
        </div>
        <h2 className="text-2xl font-semibold text-white">Calculator Not Found</h2>
        <p className="text-sm text-slate-400 font-light">
          The calculator you are looking for does not exist or has been moved.
        </p>
        <Link 
          to="/"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-cyan-500 to-indigo-600 hover:from-cyan-400 hover:to-indigo-500 text-white text-xs font-semibold rounded-xl transition-all"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Return to Home</span>
        </Link>
      </div>
    );
  }

  // Get up to 4 other tools in the same category for SEO internal linking
  const relatedCalculators = allTools
    .filter(t => t.category === tool.category && t.id !== tool.id)
    .slice(0, 4);

  // Dynamic Page Metadata matching requested standard formula guidelines
  const displayTitle = `${tool.name} - Free Online ${categoryMeta?.name || "Financial"} Calculator`;
  const displayDescription = `Calculate ${tool.description.toLowerCase()} with our free ${tool.name.toLowerCase()}. Review parameters, schedules, visualizations and custom insights.`;

  const breadcrumbs = useMemo(() => [
    { name: "Home", url: "/" },
    { name: categoryMeta?.name || "Calculators", url: "/" },
    { name: tool.name, url: `/${slug}` }
  ], [categoryMeta, tool, slug]);

  const faqs = useMemo(() => tool.guide?.faq || [], [tool]);

  const calculatorData = useMemo(() => ({
    name: tool.name,
    description: tool.description,
    category: categoryMeta?.name || "Financial Utility"
  }), [tool, categoryMeta]);

  return (
    <div className="space-y-12 py-6">
      <SEO 
        title={displayTitle} 
        description={displayDescription} 
        type="calculator"
        breadcrumbs={breadcrumbs}
        faqs={faqs}
        calculatorData={calculatorData}
      />

      {/* ================= BREADCRUMB NAVIGATION ================= */}
      <nav className="flex items-center gap-2 text-xs text-slate-400 font-mono font-light select-none">
        <Link to="/" className="hover:text-cyan-400 transition-colors">Home</Link>
        <ChevronRight className="w-3 h-3 text-slate-600" />
        <span className="text-slate-500 font-mono">{categoryMeta?.name || "Utilities"}</span>
        <ChevronRight className="w-3 h-3 text-slate-600" />
        <span className="text-slate-200 truncate">{tool.name}</span>
      </nav>

      {/* ================= TITLE & HEADERS ================= */}
      <div className="space-y-6 text-left">
        <div className="space-y-3">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-white tracking-tight">
            {tool.name}
          </h1>
          <p className="text-slate-400 text-xs sm:text-sm font-light max-w-3xl leading-relaxed">
            {tool.description}
          </p>
        </div>
      </div>

      {/* ================= INTERACTIVE WORKSPACE GRID ================= */}
      <div className={`grid grid-cols-1 gap-8 items-start transition-all duration-500 ease-in-out ${
        hasCalculated ? "lg:grid-cols-12" : "max-w-2xl mx-auto w-full"
      }`}>
        
        {/* Left Side: Parameters Inputs Card */}
        <div className={`bg-slate-950/40 border border-slate-900 rounded-2xl p-6 shadow-xl space-y-6 transition-all duration-500 ${
          hasCalculated ? "lg:col-span-5" : "w-full"
        }`}>
          <div className="flex items-center justify-between border-b border-slate-900 pb-3">
            <h2 className="text-sm font-bold text-white uppercase font-mono tracking-wider flex items-center gap-2">
              <Calculator className="w-4 h-4 text-cyan-400" />
              Adjust Parameters
            </h2>
            <span className="text-[10px] text-slate-500 font-mono uppercase bg-slate-900/50 px-2 py-0.5 rounded border border-slate-800">
              Interactive Form
            </span>
          </div>

          <div className="space-y-5">
            {tool.inputs.map((input) => (
              <div key={input.id} className="space-y-2 select-none" id={`input-group-${input.id}`}>
                <div className="flex items-center justify-between">
                  <label className="text-xs text-slate-300 font-semibold tracking-tight">
                    {input.label}
                  </label>
                  {input.helpText && (
                    <div className="group relative">
                      <Info className="w-3.5 h-3.5 text-slate-500 hover:text-slate-300 cursor-help transition-colors" />
                      <div className="absolute right-0 bottom-full mb-2 hidden group-hover:block w-48 p-2.5 bg-slate-900 text-slate-200 text-[10px] rounded-lg leading-relaxed shadow-xl border border-slate-800 font-sans z-50">
                        {input.helpText}
                      </div>
                    </div>
                  )}
                </div>

                {input.type === "select" ? (
                  <select
                    value={inputVals[input.id] ?? input.defaultValue}
                    onChange={(e) => handleInputChange(input.id, parseFloat(e.target.value))}
                    className="w-full bg-slate-950 border border-slate-800 py-2.5 px-3 rounded-xl text-xs text-slate-200 font-medium outline-none transition-all focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                  >
                    {input.options?.map((opt) => (
                      <option key={opt.value} value={opt.value} className="bg-slate-950 text-slate-200 font-sans">
                        {opt.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="space-y-2">
                    <div className="relative">
                      {input.type === "currency" && (
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-500 font-mono">
                          $
                        </span>
                      )}
                      <input 
                        type="number"
                        min={input.min}
                        max={input.max}
                        step={input.step}
                        value={inputVals[input.id] ?? ""}
                        onChange={(e) => {
                          const parseVal = parseFloat(e.target.value);
                          handleInputChange(input.id, isNaN(parseVal) ? 0 : parseVal);
                        }}
                        className={`w-full bg-slate-900/60 border border-slate-800 py-2.5 pr-3 rounded-xl text-xs text-slate-100 font-mono font-medium outline-none transition-all focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 ${
                          input.type === "currency" ? "pl-7" : "pl-3"
                        }`}
                      />
                      {input.type === "percentage" && (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500 font-mono">
                          %
                        </span>
                      )}
                    </div>

                    {input.min !== undefined && input.max !== undefined && (
                      <input
                        type="range"
                        min={input.min}
                        max={input.max}
                        step={input.step || 1}
                        value={inputVals[input.id] ?? input.defaultValue}
                        onChange={(e) => handleInputChange(input.id, parseFloat(e.target.value))}
                        className="w-full h-1 bg-slate-900 rounded-lg appearance-none cursor-pointer accent-cyan-500"
                      />
                    )}
                    <div className="flex justify-between text-[10px] text-slate-500 font-mono font-light">
                      <span>Min: {input.min ?? 0}</span>
                      <span>Max: {input.max ?? "No limits"}</span>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* Manual Calculate Action trigger */}
            <div className="pt-4 border-t border-slate-900/80">
              <button
                onClick={handleCalculate}
                className="w-full py-3.5 px-6 rounded-xl font-semibold text-sm transition-all duration-300 flex items-center justify-center gap-2 shadow-lg border hover:scale-[1.01] cursor-pointer bg-gradient-to-r from-cyan-500 to-indigo-600 hover:from-cyan-400 hover:to-indigo-500 text-white border-cyan-400/20 shadow-cyan-500/10 hover:shadow-cyan-400/20"
              >
                <Sparkles className="w-4 h-4 text-amber-300 animate-pulse" />
                <span>Calculate &amp; Show Results</span>
              </button>
            </div>


          </div>
        </div>

        {/* Right Side: Results Showcase */}
        {hasCalculated && (
          <div className="lg:col-span-7 bg-slate-950/60 backdrop-blur-md border border-slate-800 rounded-2xl p-6 shadow-xl space-y-8 flex flex-col justify-center min-h-[420px] animate-fade-in text-left">
            {/* Output Outcomes */}
            <div className="space-y-4 animate-fade-in bg-slate-900/10 p-5 rounded-2xl border border-cyan-500/10 shadow-[0_0_20px_rgba(6,182,212,0.05)]">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-slate-900 pb-2">
                <h3 className="font-mono text-[10px] font-bold text-slate-400 uppercase tracking-widest block">
                  COMPUTED CALCULATIONS OUTCOMES
                </h3>
                
                {/* Currency select dropdown */}
                <div className="flex items-center gap-2 self-start sm:self-auto bg-slate-950/80 border border-slate-800/80 px-2.5 py-1 rounded-lg">
                  <Coins className="w-3.5 h-3.5 text-cyan-400" />
                  <span className="text-[10px] font-mono text-slate-450">Display Currency:</span>
                  <select
                    value={selectedCurrency.code}
                    onChange={(e) => {
                      const cur = currencies.find(c => c.code === e.target.value);
                      if (cur) setSelectedCurrency(cur);
                    }}
                    className="bg-transparent border-none text-[10px] font-mono text-cyan-450 focus:ring-0 focus:outline-none cursor-pointer pr-1"
                  >
                    {currencies.map(c => (
                      <option key={c.code} value={c.code} className="bg-slate-950 text-slate-200">
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {calculationResult?.outputs.map((out: any, idx: number) => (
                  <div 
                    key={idx} 
                    className={`p-4 rounded-xl border transition-all ${
                      out.highlighted 
                        ? "bg-emerald-950/30 border-emerald-800/60 text-emerald-400" 
                        : "bg-slate-900/40 border-slate-800/80 text-slate-300"
                    }`}
                  >
                    <p className="text-[10px] font-mono tracking-wide uppercase opacity-75">
                      {out.label}
                    </p>
                    <p className="text-lg sm:text-2xl font-mono font-semibold mt-1.5 break-words">
                      {out.type === "currency" && typeof out.value === "number" && (
                        `${selectedCurrency.symbol}${(out.value * selectedCurrency.rate).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      )}
                      {out.type === "percentage" && typeof out.value === "number" && (
                        `${out.value.toFixed(2)}%`
                      )}
                      {out.type === "number" && typeof out.value === "number" && (
                        out.value.toLocaleString("en-US")
                      )}
                      {out.type === "text" && (
                        out.value
                      )}
                      {typeof out.value === "string" && out.type !== "text" && out.value}
                    </p>
                  </div>
                ))}
              </div>

              {calculationResult?.recommendation && (
                <div className="p-4 bg-amber-950/20 rounded-xl border border-amber-900/40 flex items-start gap-3 text-amber-300 text-xs leading-relaxed font-light self-start">
                  <TrendingUp className="w-4.5 h-4.5 text-amber-400 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-amber-200">System Recommendation:</span> {calculationResult.recommendation}
                  </div>
                </div>
              )}
            </div>

            {/* Graphical Projection Visualizer */}
            {calculationResult?.chartData && calculationResult.chartData.length > 0 && (
              <div className="space-y-4 pt-2">
                <h4 className="text-[10px] text-slate-400 font-mono uppercase tracking-widest block border-b border-slate-900 pb-2">
                  Interactive Visual Dynamic Projection
                </h4>
                
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={calculationResult.chartData}>
                      <XAxis dataKey="name" stroke="#475569" fontSize={10} tickLine={false} axisLine={false} />
                      <YAxis stroke="#475569" fontSize={10} tickLine={false} axisLine={false} tickFormatter={(val) => `${selectedCurrency.symbol}${(val * selectedCurrency.rate).toLocaleString("en-US", { maximumFractionDigits: 0 })}`} />
                      <ChartTooltip 
                        contentStyle={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', borderRadius: '8px' }}
                        itemStyle={{ color: 'var(--text-primary)' }}
                        labelClassName="text-slate-400 text-xs font-mono"
                        formatter={(value) => [`${selectedCurrency.symbol}${(parseFloat(value as string) * selectedCurrency.rate).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, `Value (${selectedCurrency.code})`]} 
                      />
                      <Bar dataKey="value" fill="#06b6d4" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Amortization schedule detailed list */}
            {calculationResult?.schedule && calculationResult.schedule.length > 0 && (
              <div className="space-y-3 pt-4 border-t border-slate-900">
                <h4 className="text-[10px] text-slate-400 font-mono uppercase tracking-widest">
                  Amortization Schedule Period Breakdown
                </h4>

                <div className="max-h-60 overflow-y-auto border border-slate-800 rounded-xl bg-[#02050c]">
                  <table className="w-full text-left border-collapse font-mono text-[11px] text-slate-300">
                    <thead>
                      <tr className="bg-slate-950 border-b border-slate-900 text-slate-400 select-none">
                        <th className="py-2.5 px-3">Period</th>
                        <th className="py-2.5 px-3 text-right">Payment</th>
                        <th className="py-2.5 px-3 text-right">Principal</th>
                        <th className="py-2.5 px-3 text-right">Interest</th>
                        <th className="py-2.5 px-3 text-right">Remaining</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-900/60">
                      {calculationResult.schedule.slice(0, 120).map((row: any) => (
                        <tr key={row.period} className="hover:bg-slate-950/40">
                          <td className="py-2 px-3">{row.period}</td>
                          <td className="py-2 px-3 text-right">{selectedCurrency.symbol}{(row.payment * selectedCurrency.rate).toFixed(2)}</td>
                          <td className="py-2 px-3 text-right">{selectedCurrency.symbol}{(row.principal * selectedCurrency.rate).toFixed(2)}</td>
                          <td className="py-2 px-3 text-right">{selectedCurrency.symbol}{(row.interest * selectedCurrency.rate).toFixed(2)}</td>
                          <td className="py-2 px-3 text-right">{selectedCurrency.symbol}{(row.balance * selectedCurrency.rate).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

      </div>

      {/* ================= CONTENT & GENERAL GUIDE SECTION ================= */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 pt-8 border-t border-slate-900/60">
        
        {/* H1 detailed copywriting and calculation guidelines */}
        <div className="md:col-span-8 space-y-6 text-left">
          <div id="handbook-container" className="text-slate-300 bg-[#040914] border border-slate-900 rounded-2xl p-6 sm:p-8 space-y-6">
            {(() => {
              const articleContent = (slug && (fireAndRetirementArticles[slug] || mortgageArticles[slug] || creditInvestmentArticles[slug])) ||
                fireAndRetirementArticles[tool.id] || mortgageArticles[tool.id] || creditInvestmentArticles[tool.id] || tool.guide?.seoContent800Words;

              if (articleContent) {
                return (
                  <div className="markdown-body text-slate-300 text-left">
                    <Markdown
                      components={{
                        h1: ({ children }) => <h1 className="text-2xl sm:text-3xl font-bold text-white font-display mb-4 border-b border-slate-800 pb-3">{children}</h1>,
                        h2: ({ children }) => <h2 className="text-xl font-semibold text-white font-display mt-8 mb-4 pb-2 border-b border-slate-900/60">{children}</h2>,
                        h3: ({ children }) => <h3 className="text-base font-semibold text-cyan-400 font-display mt-6 mb-3">{children}</h3>,
                        h4: ({ children }) => <h4 className="text-sm font-semibold text-slate-200 mt-4 mb-2">{children}</h4>,
                        p: ({ children }) => <p className="text-xs sm:text-sm text-slate-300 font-light leading-relaxed mb-4">{children}</p>,
                        ul: ({ children }) => <ul className="list-disc pl-5 space-y-2 mb-4 text-slate-300 text-xs sm:text-sm font-light">{children}</ul>,
                        ol: ({ children }) => <ol className="list-decimal pl-5 space-y-2 mb-4 text-slate-300 text-xs sm:text-sm font-light">{children}</ol>,
                        li: ({ children }) => <li className="leading-relaxed mb-1">{children}</li>,
                        strong: ({ children }) => <strong className="font-semibold text-slate-100">{children}</strong>,
                        a: ({ href, children }) => {
                          const isInternal = href && (href.startsWith("/") || href.startsWith("https://usmoneyai.com") || href.startsWith("http://localhost"));
                          let toPath = href || "";
                          if (href && (href.startsWith("https://usmoneyai.com") || href.startsWith("http://localhost"))) {
                            try {
                              const urlObj = new URL(href);
                              toPath = urlObj.pathname;
                            } catch (e) {
                              toPath = href;
                            }
                          }
                          if (isInternal) {
                            return (
                              <Link 
                                to={toPath} 
                                className="text-cyan-400 hover:text-cyan-300 underline underline-offset-2 transition-colors"
                              >
                                {children}
                              </Link>
                            );
                          }
                          return (
                            <a 
                              href={href} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              className="text-cyan-400 hover:text-cyan-300 underline underline-offset-2 transition-colors"
                            >
                              {children}
                            </a>
                          );
                        },
                        code: ({ children }) => <code className="bg-slate-950 px-1.5 py-0.5 rounded text-cyan-300 font-mono text-xs border border-slate-900">{children}</code>
                      }}
                    >
                      {articleContent}
                    </Markdown>
                  </div>
                );
              }

              return (
                <>
                  <div className="flex items-center gap-2 text-cyan-400 font-semibold text-sm">
                    <BookOpen className="w-4.5 h-4.5" />
                    <span>Comprehensive Handbook &amp; Equations</span>
                  </div>
                  
                  <div className="space-y-4">
                    <h2 className="text-xl font-semibold text-white font-display">Overview and Purpose</h2>
                    <p className="text-xs sm:text-sm text-slate-400 font-light leading-relaxed">
                      {tool.guide.overview}
                    </p>
                  </div>

                  <div className="space-y-4 pt-4 border-t border-slate-900/60">
                    <h2 className="text-xl font-semibold text-white font-display">
                      How to Use This Calculator
                    </h2>
                    <p className="text-xs sm:text-sm text-slate-400 font-light leading-relaxed whitespace-pre-wrap">
                      {tool.guide.howToUse}
                    </p>
                  </div>

                  <div className="space-y-4 pt-4 border-t border-slate-900/60">
                    <h2 className="text-xl font-semibold text-white font-display">Formula Explanation</h2>
                    <p className="text-xs sm:text-sm text-slate-400 font-light leading-relaxed whitespace-pre-wrap bg-[#02050c] p-4 rounded-xl border border-slate-900 text-cyan-300 font-mono">
                      {tool.guide.formulaExplanation}
                    </p>
                  </div>
                </>
              );
            })()}
          </div>
        </div>

        {/* Right column: FAQ & Guides */}
        <div className="md:col-span-4 space-y-6">
          <div id="faq-sidebar" className="bg-slate-950/40 border border-slate-900 rounded-2xl p-6 space-y-6 text-left">
            <div className="flex items-center gap-2 text-cyan-400 font-semibold text-sm">
              <HelpCircle className="w-4.5 h-4.5" />
              <span>Calculator FAQs</span>
            </div>

            <div className="space-y-4">
              {tool.guide.faq && tool.guide.faq.length > 0 ? (
                tool.guide.faq.map((faqItem, idx) => {
                  const isOpen = openFaqIndex === idx;
                  return (
                    <div key={idx} className="border-b border-slate-900 pb-3">
                      <button
                        onClick={() => setOpenFaqIndex(isOpen ? null : idx)}
                        className="w-full flex items-center justify-between text-left font-medium text-slate-200 hover:text-white pb-2 focus:outline-none"
                      >
                        <span className="text-xs font-semibold leading-normal pr-2">
                          {faqItem.question}
                        </span>
                        <ChevronDown className={`w-3.5 h-3.5 text-slate-500 shrink-0 transition-transform ${isOpen ? "rotate-180 text-cyan-400" : ""}`} />
                      </button>
                      
                      <div className={`transition-all duration-200 overflow-hidden ${isOpen ? "max-h-[800px] opacity-100 mt-2" : "max-h-0 opacity-0 pointer-events-none"}`}>
                        <p className="text-[11px] text-slate-400 font-light leading-relaxed">
                          {faqItem.answer}
                        </p>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-6 text-xs text-slate-500 font-light">
                  No FAQs on record for this specific tool. Check back soon for interactive updates.
                </div>
              )}
            </div>
          </div>
        </div>

      </div>

      {/* ================= RELATED CALCULATORS (SEO INTERNAL LINKING) ================= */}
      {relatedCalculators.length > 0 && (
        <div className="space-y-6 text-left pt-6 border-t border-slate-900">
          <h3 className="text-base font-semibold text-white uppercase font-mono tracking-widest">
            Related {categoryMeta?.name || "Financial"} Estimators
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {relatedCalculators.map((relatedTool) => {
              const rSlug = getSlug(relatedTool.name);
              return (
                <Link 
                  key={relatedTool.id}
                  to={`/${rSlug}`}
                  className="bg-[#0c1322] border border-[#1E293B] rounded-xl p-5 hover:border-slate-700 hover:bg-[#121c31] transition-all group flex flex-col justify-between h-34 shadow-sm"
                >
                  <div className="space-y-1">
                    <h4 className="text-sm font-semibold text-slate-200 group-hover:text-amber-400 transition-colors line-clamp-1">
                      {relatedTool.name}
                    </h4>
                    <p className="text-xs text-slate-400 font-light line-clamp-2 leading-relaxed">
                      {relatedTool.description}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 text-[11px] text-amber-500 font-sans mt-2 font-semibold">
                    <span>Calculate Now</span>
                    <ChevronRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

    </div>
  );
}
